import {
  canonicalJson,
  sha256Hex,
} from '../../reportGeneration/reportContractHash';

/**
 * Declarative custom-component package foundation.
 *
 * Contract v1 deliberately contains no executable expression, callback, or
 * source-code field. It records the shape a future isolated package worker
 * would have to implement and test; this browser/server contract never runs
 * that implementation and never promotes a declared signature to trust.
 */
export const CUSTOM_COMPONENT_PACKAGE_CONTRACT_VERSION = 1 as const;
export const CUSTOM_COMPONENT_PACKAGE_MAX_BYTES = 128_000;
export const CUSTOM_COMPONENT_PACKAGE_NAME_MAX_LENGTH = 512;
export const CUSTOM_COMPONENT_PARAMETER_STRING_DEFAULT_MAX_LENGTH = 4_096;
export const CUSTOM_COMPONENT_PARAMETER_ENUM_VALUE_MAX_LENGTH = 256;

export const CUSTOM_COMPONENT_CANONICAL_UNITS = Object.freeze({
  dimensionless: ['1'],
  pressure: ['Pa'],
  temperature: ['K'],
  mass_flow: ['kg/s'],
  specific_enthalpy: ['J/kg'],
  specific_entropy: ['J/(kg*K)'],
  heat_flow: ['W'],
  power: ['W'],
  mass: ['kg'],
  energy: ['J'],
  length: ['m'],
  area: ['m^2'],
  volume: ['m^3'],
  time: ['s'],
  angular_speed: ['rad/s'],
  torque: ['N*m'],
  voltage: ['V'],
  current: ['A'],
  frequency: ['Hz'],
  signal: ['1'],
} as const);

export type CustomComponentDomain =
  | 'fluid'
  | 'thermal'
  | 'mechanical'
  | 'electrical'
  | 'signal';
export type CustomComponentUnitQuantity = keyof typeof CUSTOM_COMPONENT_CANONICAL_UNITS;
export type CustomComponentPortDirection = 'inlet' | 'outlet' | 'bidirectional';
export type CustomComponentVariableRole = 'effort' | 'flow' | 'state' | 'signal';

export interface CustomComponentUnitRefV1 {
  quantity: CustomComponentUnitQuantity;
  canonicalUnit: string;
}

export interface CustomComponentPortVariableV1 {
  id: string;
  label: string;
  role: CustomComponentVariableRole;
  unit: CustomComponentUnitRefV1;
}

export interface CustomComponentPortV1 {
  id: string;
  label: string;
  domain: CustomComponentDomain;
  direction: CustomComponentPortDirection;
  variables: CustomComponentPortVariableV1[];
}

export type CustomComponentParameterValueType = 'number' | 'string' | 'boolean' | 'enum';

export interface CustomComponentParameterV1 {
  id: string;
  label: string;
  valueType: CustomComponentParameterValueType;
  required: boolean;
  description?: string;
  unit?: CustomComponentUnitRefV1;
  defaultValue?: string | number | boolean;
  minimum?: number;
  maximum?: number;
  enumValues?: string[];
}

export interface CustomComponentInputReferenceV1 {
  kind: 'port-variable' | 'parameter';
  /** `portId.variableId` for port variables; the parameter id otherwise. */
  reference: string;
}

export interface CustomComponentResidualDeclarationV1 {
  id: string;
  label: string;
  description: string;
  unit: CustomComponentUnitRefV1;
  dependsOn: CustomComponentInputReferenceV1[];
}

export interface CustomComponentJacobianDeclarationV1 {
  format: 'declared-sparsity';
  entries: Array<{
    residualId: string;
    withRespectTo: CustomComponentInputReferenceV1;
  }>;
}

export interface CustomComponentFeasibilityHookDeclarationV1 {
  id: string;
  label: string;
  description: string;
  severity: 'error' | 'warning';
  scope: CustomComponentInputReferenceV1[];
}

export interface CustomComponentConservationHookDeclarationV1 {
  id: string;
  label: string;
  ledger: 'mass' | 'energy' | 'momentum' | 'charge';
  unit: CustomComponentUnitRefV1;
  portVariables: string[];
  signConvention: 'positive-into-component';
}

export interface CustomComponentPackageTestV1 {
  id: string;
  name: string;
  kind: 'contract' | 'positive' | 'negative' | 'conservation';
  fixtureHash: string;
  expectedOutcome: 'accept' | 'reject';
}

export interface CustomComponentPackageContentV1 {
  contractVersion: typeof CUSTOM_COMPONENT_PACKAGE_CONTRACT_VERSION;
  packageId: string;
  packageVersion: string;
  name: string;
  description?: string;
  status: 'draft';
  ports: CustomComponentPortV1[];
  parameters: CustomComponentParameterV1[];
  residuals: CustomComponentResidualDeclarationV1[];
  jacobian?: CustomComponentJacobianDeclarationV1;
  hooks: {
    feasibility: CustomComponentFeasibilityHookDeclarationV1[];
    conservation: CustomComponentConservationHookDeclarationV1[];
  };
  tests: CustomComponentPackageTestV1[];
}

export type CustomComponentPackageSignatureV1 =
  | { algorithm: 'none'; keyId: null; value: null }
  | { algorithm: 'ed25519'; keyId: string; value: string };

export interface CustomComponentPackageV1 {
  content: CustomComponentPackageContentV1;
  contentHash: string;
  signature: CustomComponentPackageSignatureV1;
}

export interface ParsedCustomComponentPackageV1 {
  package: CustomComponentPackageV1;
  signatureTrust: 'unsigned' | 'declared-signature-unverified';
  /** Contract v1 is a package-description preview, never browser-executable physics. */
  executionStatus: 'non-executable-preview';
}

export interface CustomComponentExecutionBlock {
  code:
    | 'custom-package-missing'
    | 'custom-package-invalid'
    | 'custom-package-unsigned'
    | 'custom-package-untrusted';
  field: 'customComponentId' | 'customComponentPackage' | 'customComponentPackage.signature';
  message: string;
  context: Readonly<Record<string, string | number | boolean | null>>;
}

const SAFE_ID = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/u;
const SEMVER = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-[0-9A-Za-z.-]+)?$/u;
const SHA256 = /^[a-f0-9]{64}$/u;
const ED25519_HEX = /^[a-f0-9]{128}$/u;
const MAX_ARRAY_LENGTH = 128;

const PORT_UNIT_QUANTITIES: Readonly<Record<CustomComponentDomain, ReadonlySet<CustomComponentUnitQuantity>>> = {
  fluid: new Set(['pressure', 'temperature', 'mass_flow', 'specific_enthalpy', 'specific_entropy']),
  thermal: new Set(['temperature', 'heat_flow', 'energy']),
  mechanical: new Set(['angular_speed', 'torque', 'power']),
  electrical: new Set(['voltage', 'current', 'power', 'frequency']),
  signal: new Set(['signal', 'dimensionless']),
};

const CONSERVATION_QUANTITY: Readonly<Record<CustomComponentConservationHookDeclarationV1['ledger'], ReadonlySet<CustomComponentUnitQuantity>>> = {
  mass: new Set(['mass_flow']),
  energy: new Set(['heat_flow', 'power']),
  momentum: new Set(['pressure']),
  charge: new Set(['current']),
};

/**
 * A conservation hook may name only a directly conserved port flux/effort.
 * State variables such as temperature or specific enthalpy remain legitimate
 * residual dependencies, but are not themselves a mass/energy ledger term.
 */
const CONSERVATION_PORT_VARIABLE_COMPATIBILITY: Readonly<Record<
  CustomComponentConservationHookDeclarationV1['ledger'],
  ReadonlySet<`${CustomComponentDomain}:${CustomComponentUnitQuantity}`>
>> = {
  mass: new Set(['fluid:mass_flow']),
  energy: new Set(['thermal:heat_flow', 'mechanical:power', 'electrical:power']),
  momentum: new Set(['fluid:pressure']),
  charge: new Set(['electrical:current']),
};

function fail(path: string, message: string): never {
  throw new TypeError(`${path}: ${message}`);
}

function record(value: unknown, path: string): Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    fail(path, 'must be an object');
  }
  return value as Record<string, unknown>;
}

function onlyKeys(value: Record<string, unknown>, allowed: readonly string[], path: string): void {
  const set = new Set(allowed);
  const unexpected = Object.keys(value).find((key) => !set.has(key));
  if (unexpected) fail(path, `contains unsupported field "${unexpected}"`);
}

function array(value: unknown, path: string): unknown[] {
  if (!Array.isArray(value)) fail(path, 'must be an array');
  if (value.length > MAX_ARRAY_LENGTH) fail(path, `must contain at most ${MAX_ARRAY_LENGTH} entries`);
  return value;
}

function string(value: unknown, path: string, max = 512): string {
  const parsed = boundedString(value, path, max);
  if (!parsed.trim()) fail(path, 'must be a non-empty string');
  return parsed;
}

function boundedString(value: unknown, path: string, max: number): string {
  if (typeof value !== 'string') fail(path, 'must be a string');
  if (value.length > max) fail(path, `must contain at most ${max} characters`);
  return value;
}

function identifier(value: unknown, path: string): string {
  const parsed = string(value, path, 128);
  if (!SAFE_ID.test(parsed)) fail(path, 'must be a path-safe identifier');
  return parsed;
}

function finite(value: unknown, path: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) fail(path, 'must be finite');
  return value;
}

function enumValue<T extends string>(value: unknown, values: readonly T[], path: string): T {
  if (typeof value !== 'string' || !values.includes(value as T)) {
    fail(path, `must be one of ${values.join(', ')}`);
  }
  return value as T;
}

function uniqueIds(values: readonly { id: string }[], path: string): void {
  const seen = new Set<string>();
  for (const value of values) {
    if (seen.has(value.id)) fail(path, `contains duplicate id "${value.id}"`);
    seen.add(value.id);
  }
}

function inputReferenceKey(value: CustomComponentInputReferenceV1): string {
  return `${value.kind}:${value.reference}`;
}

function assertUniqueInputReferences(
  values: readonly CustomComponentInputReferenceV1[],
  path: string,
): void {
  const seen = new Set<string>();
  values.forEach((value, index) => {
    const key = inputReferenceKey(value);
    if (seen.has(key)) {
      fail(`${path}[${index}]`, `duplicates ${value.kind} reference "${value.reference}"`);
    }
    seen.add(key);
  });
}

function parseUnit(value: unknown, path: string): CustomComponentUnitRefV1 {
  const row = record(value, path);
  onlyKeys(row, ['quantity', 'canonicalUnit'], path);
  const quantity = enumValue(
    row.quantity,
    Object.keys(CUSTOM_COMPONENT_CANONICAL_UNITS) as CustomComponentUnitQuantity[],
    `${path}.quantity`,
  );
  const canonicalUnit = string(row.canonicalUnit, `${path}.canonicalUnit`, 64);
  if (!(CUSTOM_COMPONENT_CANONICAL_UNITS[quantity] as readonly string[]).includes(canonicalUnit)) {
    fail(`${path}.canonicalUnit`, `is not canonical for ${quantity}`);
  }
  return { quantity, canonicalUnit };
}

function parseInputReference(value: unknown, path: string): CustomComponentInputReferenceV1 {
  const row = record(value, path);
  onlyKeys(row, ['kind', 'reference'], path);
  return {
    kind: enumValue(row.kind, ['port-variable', 'parameter'] as const, `${path}.kind`),
    reference: string(row.reference, `${path}.reference`, 257),
  };
}

function assertInputReference(
  value: CustomComponentInputReferenceV1,
  portVariables: ReadonlySet<string>,
  parameters: ReadonlySet<string>,
  path: string,
): void {
  const valid = value.kind === 'port-variable'
    ? portVariables.has(value.reference)
    : parameters.has(value.reference);
  if (!valid) fail(path, `references unknown ${value.kind} "${value.reference}"`);
}

function parsePort(value: unknown, path: string): CustomComponentPortV1 {
  const row = record(value, path);
  onlyKeys(row, ['id', 'label', 'domain', 'direction', 'variables'], path);
  const domain = enumValue(
    row.domain,
    ['fluid', 'thermal', 'mechanical', 'electrical', 'signal'] as const,
    `${path}.domain`,
  );
  const variables = array(row.variables, `${path}.variables`).map((entry, index) => {
    const variablePath = `${path}.variables[${index}]`;
    const variable = record(entry, variablePath);
    onlyKeys(variable, ['id', 'label', 'role', 'unit'], variablePath);
    const unit = parseUnit(variable.unit, `${variablePath}.unit`);
    if (!PORT_UNIT_QUANTITIES[domain].has(unit.quantity)) {
      fail(`${variablePath}.unit.quantity`, `${unit.quantity} is not valid on a ${domain} port`);
    }
    return {
      id: identifier(variable.id, `${variablePath}.id`),
      label: string(variable.label, `${variablePath}.label`),
      role: enumValue(variable.role, ['effort', 'flow', 'state', 'signal'] as const, `${variablePath}.role`),
      unit,
    };
  });
  uniqueIds(variables, `${path}.variables`);
  return {
    id: identifier(row.id, `${path}.id`),
    label: string(row.label, `${path}.label`),
    domain,
    direction: enumValue(row.direction, ['inlet', 'outlet', 'bidirectional'] as const, `${path}.direction`),
    variables,
  };
}

function parseParameter(value: unknown, path: string): CustomComponentParameterV1 {
  const row = record(value, path);
  onlyKeys(row, [
    'id', 'label', 'valueType', 'required', 'description', 'unit', 'defaultValue',
    'minimum', 'maximum', 'enumValues',
  ], path);
  const valueType = enumValue(row.valueType, ['number', 'string', 'boolean', 'enum'] as const, `${path}.valueType`);
  if (typeof row.required !== 'boolean') fail(`${path}.required`, 'must be boolean');
  const parsed: CustomComponentParameterV1 = {
    id: identifier(row.id, `${path}.id`),
    label: string(row.label, `${path}.label`),
    valueType,
    required: row.required,
  };
  if (row.description !== undefined) parsed.description = string(row.description, `${path}.description`, 4096);
  if (valueType === 'number') {
    if (row.unit === undefined) fail(`${path}.unit`, 'is required for a number parameter');
    parsed.unit = parseUnit(row.unit, `${path}.unit`);
    if (row.defaultValue !== undefined) parsed.defaultValue = finite(row.defaultValue, `${path}.defaultValue`);
    if (row.minimum !== undefined) parsed.minimum = finite(row.minimum, `${path}.minimum`);
    if (row.maximum !== undefined) parsed.maximum = finite(row.maximum, `${path}.maximum`);
    if (parsed.minimum !== undefined && parsed.maximum !== undefined && parsed.minimum > parsed.maximum) {
      fail(path, 'minimum must not exceed maximum');
    }
    if (typeof parsed.defaultValue === 'number') {
      if (parsed.minimum !== undefined && parsed.defaultValue < parsed.minimum) {
        fail(`${path}.defaultValue`, `must not be less than minimum ${parsed.minimum}`);
      }
      if (parsed.maximum !== undefined && parsed.defaultValue > parsed.maximum) {
        fail(`${path}.defaultValue`, `must not exceed maximum ${parsed.maximum}`);
      }
    }
  } else {
    if (row.unit !== undefined || row.minimum !== undefined || row.maximum !== undefined) {
      fail(path, `${valueType} parameters cannot declare units or numeric bounds`);
    }
    if (valueType === 'boolean' && row.defaultValue !== undefined && typeof row.defaultValue !== 'boolean') {
      fail(`${path}.defaultValue`, 'must be boolean');
    }
    if (valueType === 'boolean' && row.defaultValue !== undefined) {
      parsed.defaultValue = row.defaultValue as boolean;
    } else if (valueType === 'string' && row.defaultValue !== undefined) {
      parsed.defaultValue = boundedString(
        row.defaultValue,
        `${path}.defaultValue`,
        CUSTOM_COMPONENT_PARAMETER_STRING_DEFAULT_MAX_LENGTH,
      );
    } else if (valueType === 'enum' && row.defaultValue !== undefined) {
      parsed.defaultValue = boundedString(
        row.defaultValue,
        `${path}.defaultValue`,
        CUSTOM_COMPONENT_PARAMETER_ENUM_VALUE_MAX_LENGTH,
      );
    }
  }
  if (valueType === 'enum') {
    const values = array(row.enumValues, `${path}.enumValues`).map((entry, index) => (
      string(entry, `${path}.enumValues[${index}]`, CUSTOM_COMPONENT_PARAMETER_ENUM_VALUE_MAX_LENGTH)
    ));
    if (values.length === 0 || new Set(values).size !== values.length) {
      fail(`${path}.enumValues`, 'must contain unique choices');
    }
    if (typeof parsed.defaultValue === 'string' && !values.includes(parsed.defaultValue)) {
      fail(`${path}.defaultValue`, 'must be one of enumValues');
    }
    parsed.enumValues = values;
  } else if (row.enumValues !== undefined) {
    fail(`${path}.enumValues`, 'is allowed only for enum parameters');
  }
  return parsed;
}

function parseResidual(value: unknown, path: string): CustomComponentResidualDeclarationV1 {
  const row = record(value, path);
  onlyKeys(row, ['id', 'label', 'description', 'unit', 'dependsOn'], path);
  const dependsOn = array(row.dependsOn, `${path}.dependsOn`).map((entry, index) => (
    parseInputReference(entry, `${path}.dependsOn[${index}]`)
  ));
  assertUniqueInputReferences(dependsOn, `${path}.dependsOn`);
  return {
    id: identifier(row.id, `${path}.id`),
    label: string(row.label, `${path}.label`),
    description: string(row.description, `${path}.description`, 4096),
    unit: parseUnit(row.unit, `${path}.unit`),
    dependsOn,
  };
}

function parseContent(value: unknown): CustomComponentPackageContentV1 {
  const row = record(value, 'customComponentPackage.content');
  onlyKeys(row, [
    'contractVersion', 'packageId', 'packageVersion', 'name', 'description', 'status',
    'ports', 'parameters', 'residuals', 'jacobian', 'hooks', 'tests',
  ], 'customComponentPackage.content');
  if (row.contractVersion !== CUSTOM_COMPONENT_PACKAGE_CONTRACT_VERSION) {
    fail('customComponentPackage.content.contractVersion', 'unsupported contract version');
  }
  const packageVersion = string(row.packageVersion, 'customComponentPackage.content.packageVersion', 128);
  if (!SEMVER.test(packageVersion)) {
    fail('customComponentPackage.content.packageVersion', 'must be semantic version x.y.z');
  }
  const ports = array(row.ports, 'customComponentPackage.content.ports')
    .map((entry, index) => parsePort(entry, `customComponentPackage.content.ports[${index}]`));
  uniqueIds(ports, 'customComponentPackage.content.ports');
  const parameters = array(row.parameters, 'customComponentPackage.content.parameters')
    .map((entry, index) => parseParameter(entry, `customComponentPackage.content.parameters[${index}]`));
  uniqueIds(parameters, 'customComponentPackage.content.parameters');
  const residuals = array(row.residuals, 'customComponentPackage.content.residuals')
    .map((entry, index) => parseResidual(entry, `customComponentPackage.content.residuals[${index}]`));
  uniqueIds(residuals, 'customComponentPackage.content.residuals');

  const portVariableDeclarations = new Map<string, {
    domain: CustomComponentDomain;
    quantity: CustomComponentUnitQuantity;
  }>(ports.flatMap((port) => (
    port.variables.map((variable) => [
      `${port.id}.${variable.id}`,
      { domain: port.domain, quantity: variable.unit.quantity },
    ] as const)
  )));
  const portVariableRefs = new Set(portVariableDeclarations.keys());
  const parameterRefs = new Set(parameters.map((parameter) => parameter.id));
  for (const [residualIndex, residual] of residuals.entries()) {
    residual.dependsOn.forEach((dependency, dependencyIndex) => {
      assertInputReference(
        dependency,
        portVariableRefs,
        parameterRefs,
        `customComponentPackage.content.residuals[${residualIndex}].dependsOn[${dependencyIndex}]`,
      );
    });
  }

  let jacobian: CustomComponentJacobianDeclarationV1 | undefined;
  if (row.jacobian !== undefined) {
    const jacobianRow = record(row.jacobian, 'customComponentPackage.content.jacobian');
    onlyKeys(jacobianRow, ['format', 'entries'], 'customComponentPackage.content.jacobian');
    if (jacobianRow.format !== 'declared-sparsity') {
      fail('customComponentPackage.content.jacobian.format', 'must be declared-sparsity');
    }
    const residualIds = new Set(residuals.map((residual) => residual.id));
    const jacobianKeys = new Set<string>();
    const entries = array(jacobianRow.entries, 'customComponentPackage.content.jacobian.entries')
      .map((entry, index) => {
        const entryPath = `customComponentPackage.content.jacobian.entries[${index}]`;
        const entryRow = record(entry, entryPath);
        onlyKeys(entryRow, ['residualId', 'withRespectTo'], entryPath);
        const residualId = identifier(entryRow.residualId, `${entryPath}.residualId`);
        if (!residualIds.has(residualId)) fail(`${entryPath}.residualId`, 'references an unknown residual');
        const withRespectTo = parseInputReference(entryRow.withRespectTo, `${entryPath}.withRespectTo`);
        assertInputReference(withRespectTo, portVariableRefs, parameterRefs, `${entryPath}.withRespectTo`);
        const jacobianKey = `${residualId}:${inputReferenceKey(withRespectTo)}`;
        if (jacobianKeys.has(jacobianKey)) {
          fail(entryPath, `duplicates Jacobian entry for residual "${residualId}" and ${withRespectTo.kind} reference "${withRespectTo.reference}"`);
        }
        jacobianKeys.add(jacobianKey);
        return { residualId, withRespectTo };
      });
    jacobian = { format: 'declared-sparsity', entries };
  }

  const hooksRow = record(row.hooks, 'customComponentPackage.content.hooks');
  onlyKeys(hooksRow, ['feasibility', 'conservation'], 'customComponentPackage.content.hooks');
  const feasibility = array(hooksRow.feasibility, 'customComponentPackage.content.hooks.feasibility')
    .map((entry, index): CustomComponentFeasibilityHookDeclarationV1 => {
      const hookPath = `customComponentPackage.content.hooks.feasibility[${index}]`;
      const hook = record(entry, hookPath);
      onlyKeys(hook, ['id', 'label', 'description', 'severity', 'scope'], hookPath);
      const scope = array(hook.scope, `${hookPath}.scope`).map((scopeEntry, scopeIndex) => {
        const reference = parseInputReference(scopeEntry, `${hookPath}.scope[${scopeIndex}]`);
        assertInputReference(reference, portVariableRefs, parameterRefs, `${hookPath}.scope[${scopeIndex}]`);
        return reference;
      });
      assertUniqueInputReferences(scope, `${hookPath}.scope`);
      return {
        id: identifier(hook.id, `${hookPath}.id`),
        label: string(hook.label, `${hookPath}.label`),
        description: string(hook.description, `${hookPath}.description`, 4096),
        severity: enumValue(hook.severity, ['error', 'warning'] as const, `${hookPath}.severity`),
        scope,
      };
    });
  uniqueIds(feasibility, 'customComponentPackage.content.hooks.feasibility');
  const conservation = array(hooksRow.conservation, 'customComponentPackage.content.hooks.conservation')
    .map((entry, index): CustomComponentConservationHookDeclarationV1 => {
      const hookPath = `customComponentPackage.content.hooks.conservation[${index}]`;
      const hook = record(entry, hookPath);
      onlyKeys(hook, ['id', 'label', 'ledger', 'unit', 'portVariables', 'signConvention'], hookPath);
      const ledger = enumValue(hook.ledger, ['mass', 'energy', 'momentum', 'charge'] as const, `${hookPath}.ledger`);
      const unit = parseUnit(hook.unit, `${hookPath}.unit`);
      if (!CONSERVATION_QUANTITY[ledger].has(unit.quantity)) {
        fail(`${hookPath}.unit.quantity`, `${unit.quantity} is not valid for the ${ledger} ledger`);
      }
      const variables = array(hook.portVariables, `${hookPath}.portVariables`).map((variable, variableIndex) => {
        const reference = string(variable, `${hookPath}.portVariables[${variableIndex}]`, 257);
        const declaration = portVariableDeclarations.get(reference);
        if (!declaration) fail(`${hookPath}.portVariables[${variableIndex}]`, 'references an unknown port variable');
        const compatibilityKey = `${declaration.domain}:${declaration.quantity}` as const;
        if (!CONSERVATION_PORT_VARIABLE_COMPATIBILITY[ledger].has(compatibilityKey)) {
          fail(
            `${hookPath}.portVariables[${variableIndex}]`,
            `${declaration.domain} port variable "${reference}" with ${declaration.quantity} quantity is not valid for the ${ledger} ledger`,
          );
        }
        return reference;
      });
      const duplicateVariable = variables.find((variable, variableIndex) => (
        variables.indexOf(variable) !== variableIndex
      ));
      if (duplicateVariable) {
        fail(`${hookPath}.portVariables`, `contains duplicate port-variable reference "${duplicateVariable}"`);
      }
      return {
        id: identifier(hook.id, `${hookPath}.id`),
        label: string(hook.label, `${hookPath}.label`),
        ledger,
        unit,
        portVariables: variables,
        signConvention: enumValue(hook.signConvention, ['positive-into-component'] as const, `${hookPath}.signConvention`),
      };
    });
  uniqueIds(conservation, 'customComponentPackage.content.hooks.conservation');

  const tests = array(row.tests, 'customComponentPackage.content.tests')
    .map((entry, index): CustomComponentPackageTestV1 => {
      const testPath = `customComponentPackage.content.tests[${index}]`;
      const test = record(entry, testPath);
      onlyKeys(test, ['id', 'name', 'kind', 'fixtureHash', 'expectedOutcome'], testPath);
      const fixtureHash = string(test.fixtureHash, `${testPath}.fixtureHash`, 64);
      if (!SHA256.test(fixtureHash)) fail(`${testPath}.fixtureHash`, 'must be a lowercase SHA-256 digest');
      return {
        id: identifier(test.id, `${testPath}.id`),
        name: string(test.name, `${testPath}.name`),
        kind: enumValue(test.kind, ['contract', 'positive', 'negative', 'conservation'] as const, `${testPath}.kind`),
        fixtureHash,
        expectedOutcome: enumValue(test.expectedOutcome, ['accept', 'reject'] as const, `${testPath}.expectedOutcome`),
      };
    });
  uniqueIds(tests, 'customComponentPackage.content.tests');

  const parsed: CustomComponentPackageContentV1 = {
    contractVersion: CUSTOM_COMPONENT_PACKAGE_CONTRACT_VERSION,
    packageId: identifier(row.packageId, 'customComponentPackage.content.packageId'),
    packageVersion,
    name: string(
      row.name,
      'customComponentPackage.content.name',
      CUSTOM_COMPONENT_PACKAGE_NAME_MAX_LENGTH,
    ),
    status: enumValue(row.status, ['draft'] as const, 'customComponentPackage.content.status'),
    ports,
    parameters,
    residuals,
    ...(jacobian ? { jacobian } : {}),
    hooks: { feasibility, conservation },
    tests,
  };
  if (row.description !== undefined) {
    parsed.description = string(row.description, 'customComponentPackage.content.description', 4096);
  }
  return parsed;
}

function parseSignature(value: unknown): CustomComponentPackageSignatureV1 {
  const row = record(value, 'customComponentPackage.signature');
  onlyKeys(row, ['algorithm', 'keyId', 'value'], 'customComponentPackage.signature');
  if (row.algorithm === 'none') {
    if (row.keyId !== null || row.value !== null) {
      fail('customComponentPackage.signature', 'unsigned identity requires null keyId and value');
    }
    return { algorithm: 'none', keyId: null, value: null };
  }
  if (row.algorithm === 'ed25519') {
    const keyId = identifier(row.keyId, 'customComponentPackage.signature.keyId');
    const signature = string(row.value, 'customComponentPackage.signature.value', 128);
    if (!ED25519_HEX.test(signature)) {
      fail('customComponentPackage.signature.value', 'must be a 64-byte lowercase hexadecimal declaration');
    }
    return { algorithm: 'ed25519', keyId, value: signature };
  }
  fail('customComponentPackage.signature.algorithm', 'is unsupported');
}

/**
 * Verify exact schema, canonical content hash and declared signature identity.
 * No trusted-key registry exists in this release, so even a syntactically
 * valid Ed25519 value remains explicitly unverified and non-executable.
 */
export function parseCustomComponentPackageV1(value: unknown): ParsedCustomComponentPackageV1 {
  const row = record(value, 'customComponentPackage');
  onlyKeys(row, ['content', 'contentHash', 'signature'], 'customComponentPackage');
  const content = parseContent(row.content);
  const canonicalContent = canonicalJson(content);
  const bytes = new TextEncoder().encode(canonicalContent).byteLength;
  if (bytes > CUSTOM_COMPONENT_PACKAGE_MAX_BYTES) {
    fail('customComponentPackage.content', `exceeds ${CUSTOM_COMPONENT_PACKAGE_MAX_BYTES} canonical bytes`);
  }
  const contentHash = string(row.contentHash, 'customComponentPackage.contentHash', 64);
  if (!SHA256.test(contentHash)) {
    fail('customComponentPackage.contentHash', 'must be a lowercase SHA-256 digest');
  }
  const expectedHash = sha256Hex(canonicalContent);
  if (contentHash !== expectedHash) {
    fail('customComponentPackage.contentHash', `does not match canonical content (expected ${expectedHash})`);
  }
  const signature = parseSignature(row.signature);
  return {
    package: { content, contentHash, signature },
    signatureTrust: signature.algorithm === 'none' ? 'unsigned' : 'declared-signature-unverified',
    executionStatus: 'non-executable-preview',
  };
}

/** Create and self-verify an explicitly unsigned, non-executable package draft. */
export function createUnsignedCustomComponentPackageDraft(
  content: Omit<CustomComponentPackageContentV1, 'contractVersion' | 'status'>,
): CustomComponentPackageV1 {
  const complete: CustomComponentPackageContentV1 = {
    contractVersion: CUSTOM_COMPONENT_PACKAGE_CONTRACT_VERSION,
    ...content,
    status: 'draft',
  };
  const candidate: CustomComponentPackageV1 = {
    content: complete,
    contentHash: sha256Hex(canonicalJson(complete)),
    signature: { algorithm: 'none', keyId: null, value: null },
  };
  return parseCustomComponentPackageV1(candidate).package;
}

/**
 * Produce the one fail-closed execution disposition used by EO readiness and
 * the legacy NS boundary. Validity or a declared signature never implies a
 * trusted implementation: this release has neither trusted keys nor an
 * isolated package worker.
 */
export function customComponentExecutionBlock(
  nodeId: string,
  label: string,
  packageValue: unknown,
): CustomComponentExecutionBlock {
  if (packageValue === undefined) {
    return {
      code: 'custom-package-missing',
      field: 'customComponentId',
      message: `${label} (${nodeId}) is a legacy custom annotation with no verified package implementation. It is preserved as data but cannot execute physics.`,
      context: { packageContractVersion: null, executionStatus: 'annotation-only' },
    };
  }
  let parsed: ParsedCustomComponentPackageV1;
  try {
    parsed = parseCustomComponentPackageV1(packageValue);
  } catch (error) {
    return {
      code: 'custom-package-invalid',
      field: 'customComponentPackage',
      message: `${label} (${nodeId}) has an invalid or tampered custom package draft: ${
        error instanceof Error ? error.message : String(error)
      }. The data is preserved but cannot execute physics.`,
      context: { packageContractVersion: null, executionStatus: 'unsupported' },
    };
  }
  const identity = `${parsed.package.content.packageId}@${parsed.package.content.packageVersion}`;
  if (parsed.signatureTrust === 'unsigned') {
    return {
      code: 'custom-package-unsigned',
      field: 'customComponentPackage.signature',
      message: `${label} (${nodeId}) uses unsigned package draft ${identity}. Its canonical hash is valid, but unsigned drafts are preview metadata and cannot execute physics.`,
      context: {
        packageContractVersion: parsed.package.content.contractVersion,
        packageIdentity: identity,
        contentHash: parsed.package.contentHash,
        signatureTrust: parsed.signatureTrust,
        executionStatus: parsed.executionStatus,
      },
    };
  }
  return {
    code: 'custom-package-untrusted',
    field: 'customComponentPackage.signature',
    message: `${label} (${nodeId}) declares package signature ${identity} with key ${parsed.package.signature.keyId}, but no configured trusted key verifies it. Declared identity never implies trust or executable physics.`,
    context: {
      packageContractVersion: parsed.package.content.contractVersion,
      packageIdentity: identity,
      contentHash: parsed.package.contentHash,
      signatureTrust: parsed.signatureTrust,
      executionStatus: parsed.executionStatus,
    },
  };
}
